import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage, useT } from "../lib/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { api } from "../lib/api";
import {
  ArrowLeft,
  Mail,
  Copy,
  Check,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Shield,
  CreditCard,
  Wrench,
  HelpCircle,
  Sparkles,
  Users,
} from "lucide-react";

const SUPPORT_EMAIL = "hey@solunary.health";

const CATEGORIES = [
  { value: "general", labelKey: "support.catGeneral", fallback: "General Inquiry", icon: HelpCircle },
  { value: "billing", labelKey: "support.catBilling", fallback: "Billing & Subscriptions", icon: CreditCard },
  { value: "technical", labelKey: "support.catTechnical", fallback: "Technical & Platform Help", icon: Wrench },
  { value: "legal", labelKey: "support.catLegal", fallback: "Legal, Privacy & Compliance", icon: Shield },
  { value: "feedback", labelKey: "support.catFeedback", fallback: "Feedback & Feature Requests", icon: Sparkles },
  { value: "partnership", labelKey: "support.catPartnership", fallback: "Partnership & Press", icon: Users },
];

export default function SupportPage() {
  const { lang } = useLanguage();
  const t = useT("saas");
  const tCommon = useT("common");

  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "general",
    subject: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ticketResult, setTicketResult] = useState(null);

  const handleCopyEmail = () => {
    navigator.clipboard?.writeText(SUPPORT_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError(t("support.errorName") || "Please enter your name.");
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      setError(t("support.errorEmail") || "Please enter a valid email address.");
      return;
    }
    if (!formData.subject.trim() || formData.subject.length < 2) {
      setError(t("support.errorSubject") || "Please enter a subject for your inquiry.");
      return;
    }
    if (!formData.message.trim() || formData.message.length < 5) {
      setError(t("support.errorMessage") || "Please describe your request in at least 5 characters.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      category: formData.category,
      subject: formData.subject.trim(),
      message: formData.message.trim(),
      source_url: window.location.pathname,
      locale: lang || "en",
    };

    try {
      const resp = await api.post("/support/ticket", payload);

      setTicketResult(resp.data);
      setFormData({
        name: "",
        email: "",
        category: "general",
        subject: "",
        message: "",
      });
    } catch (err) {
      // The server's `detail` is a code or an English diagnostic (a rate-limit refusal
      // answers "RATE_LIMITED"), never a sentence for the visitor.
      setError(t("support.errorGeneric") || "Failed to submit support ticket. Please write to hey@solunary.health directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sol-ivory flex flex-col justify-between" data-testid="support-page">
      {/* Navigation Header */}
      <header className="bg-sol-midnight text-sol-ivory border-b border-sol-ivory/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/platform" className="flex items-center gap-3">
            <img src="/brand/solunary-logo.png" alt="Solunary" className="h-10 w-10 object-contain" />
            <span className="font-sol font-semibold text-lg text-sol-ivory">Solunary</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link
              to="/platform"
              className="flex items-center gap-1.5 text-xs text-sol-ivory/70 hover:text-sol-gold transition font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{tCommon("actions.back") || "Back"}</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-1 w-full">
        {/* Page Heading */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sol-gold/15 text-sol-midnight font-medium text-xs mb-3">
            <HelpCircle className="w-3.5 h-3.5 text-sol-gold" />
            <span>{t("support.badge") || "Support & Contact"}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-sol-midnight mb-4">
            {t("support.pageTitle") || "How can we assist you?"}
          </h1>
          <p className="text-sol-midnight/70 text-base leading-relaxed">
            {t("support.pageSubtitle") ||
              "Whether you are exploring Solunary, managing your practice, or have legal questions, our team is here to help."}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Direct Info Cards */}
          <div className="lg:col-span-5 space-y-6">
            {/* Primary Email Card */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-sol-midnight/10 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-sol-gold/15 text-sol-midnight flex items-center justify-center">
                  <Mail className="w-6 h-6 text-sol-gold" />
                </div>
                <div>
                  <h2 className="font-sol font-semibold text-lg text-sol-midnight">
                    {t("support.directEmailTitle") || "Direct Email"}
                  </h2>
                  <p className="text-xs text-sol-midnight/60">
                    {t("support.directEmailSub") || "Reach the whole team"}
                  </p>
                </div>
              </div>

              <div className="p-3.5 bg-sol-ivory/60 rounded-2xl border border-sol-midnight/10 flex items-center justify-between mb-4">
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid="support-direct-email-link"
                  className="font-mono text-sm font-semibold text-sol-midnight hover:text-sol-gold transition"
                >
                  {SUPPORT_EMAIL}
                </a>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="p-1.5 rounded-lg text-sol-midnight/60 hover:text-sol-midnight hover:bg-sol-midnight/5 transition"
                  title="Copy email address"
                  aria-label="Copy email"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              <p className="text-xs text-sol-midnight/70 leading-relaxed">
                {t("support.emailHelpNote") ||
                  "All inquiries sent to this address are automatically routed to our support, billing, and technical leads with reply tracking."}
              </p>
            </div>

            {/* Quick Links Card */}
            <div className="bg-sol-midnight text-sol-ivory rounded-3xl p-6 sm:p-8 shadow-sm">
              <h2 className="font-sol font-semibold text-lg mb-3">
                {t("support.helpfulResources") || "Helpful Resources"}
              </h2>
              <ul className="space-y-3 text-xs text-sol-ivory/75">
                <li>
                  <Link to="/terms" className="hover:text-sol-gold transition inline-flex items-center gap-2">
                    <Shield className="w-4 h-4 text-sol-gold shrink-0" />
                    <span>{t("footer.terms") || "Terms and Conditions"}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/legal/privacy" className="hover:text-sol-gold transition inline-flex items-center gap-2">
                    <Shield className="w-4 h-4 text-sol-gold shrink-0" />
                    <span>{t("footer.privacy") || "Privacy Policy"}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/platform#pricing" className="hover:text-sol-gold transition inline-flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-sol-gold shrink-0" />
                    <span>{t("nav.pricing") || "Pricing & Plans"}</span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column: Ticket Submission Card */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-sol-midnight/10 shadow-sm">
              {ticketResult ? (
                /* Success Screen */
                <div data-testid="support-page-success" className="text-center py-8">
                  <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-100">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>

                  <h3 className="font-display text-2xl text-sol-midnight mb-2">
                    {t("support.successTitle") || "Inquiry Received!"}
                  </h3>

                  <p className="text-sm text-sol-midnight/70 leading-relaxed max-w-md mx-auto mb-6">
                    {t("support.successMessage") ||
                      "Thank you for contacting Solunary. We have dispatched your inquiry to our support team and sent a confirmation receipt to your email."}
                  </p>

                  <div className="bg-sol-ivory/60 rounded-2xl p-5 border border-sol-midnight/8 max-w-sm mx-auto mb-6">
                    <div className="text-xs uppercase tracking-wider text-sol-midnight/50 mb-1">
                      {t("support.ticketReference") || "Ticket Reference"}
                    </div>
                    <div
                      data-testid="support-page-ticket-number"
                      className="font-mono text-xl font-bold text-sol-midnight tracking-wider"
                    >
                      {ticketResult.ticket_number}
                    </div>
                  </div>

                  <div className="p-3 bg-sol-gold/10 border border-sol-gold/20 rounded-xl text-xs text-sol-midnight/80 mb-6 flex items-center justify-center gap-2 max-w-sm mx-auto">
                    <Mail className="w-4 h-4 text-sol-gold shrink-0" />
                    <span>
                      Direct mailbox:{" "}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold underline hover:text-sol-gold">
                        {SUPPORT_EMAIL}
                      </a>
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setTicketResult(null)}
                    className="px-6 py-2.5 rounded-xl border border-sol-midnight/15 text-xs font-semibold text-sol-midnight hover:bg-sol-midnight/5 transition"
                  >
                    {t("support.anotherInquiry") || "Send another inquiry"}
                  </button>
                </div>
              ) : (
                /* Ticket Submission Form */
                <div>
                  <h2 className="font-sol font-semibold text-xl text-sol-midnight mb-1">
                    {t("support.formHeading") || "Submit a Support Ticket"}
                  </h2>
                  <p className="text-xs text-sol-midnight/60 mb-6">
                    {t("support.formHelp") ||
                      "Our team typically answers within 24 business hours. A copy will be emailed to your inbox."}
                  </p>

                  {error && (
                    <div
                      data-testid="support-page-error"
                      className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs text-rose-800 flex items-start gap-2.5"
                    >
                      <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{error}</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                          {t("support.nameLabel") || "Your Name"} <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          data-testid="support-page-input-name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="e.g. Marie Laurent"
                          className="w-full px-4 py-3 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                          {t("support.emailLabel") || "Your Email"} <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          data-testid="support-page-input-email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="name@example.com"
                          className="w-full px-4 py-3 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                        {t("support.categoryLabel") || "Inquiry Category"}
                      </label>
                      <select
                        name="category"
                        data-testid="support-page-select-category"
                        value={formData.category}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20"
                      >
                        {CATEGORIES.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {t(cat.labelKey) || cat.fallback}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                        {t("support.subjectLabel") || "Subject"} <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="subject"
                        data-testid="support-page-input-subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder={t("support.subjectPlaceholder") || "Brief summary of your question"}
                        className="w-full px-4 py-3 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                        {t("support.messageLabel") || "Message"} <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        data-testid="support-page-input-message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={
                          t("support.messagePlaceholder") ||
                          "Provide any details so our team can assist you efficiently..."
                        }
                        className="w-full px-4 py-3 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20 resize-y"
                      />
                    </div>

                    <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <span className="text-[11px] text-sol-midnight/50">
                        Target inbox:{" "}
                        <a href={`mailto:${SUPPORT_EMAIL}`} className="underline font-medium hover:text-sol-gold">
                          {SUPPORT_EMAIL}
                        </a>
                      </span>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="support-page-submit-btn"
                        className="w-full sm:w-auto px-8 py-3 rounded-xl bg-sol-midnight text-sol-gold font-semibold text-xs hover:bg-sol-midnight/90 disabled:opacity-50 transition inline-flex items-center justify-center gap-2 shadow-sm"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>{t("support.submitting") || "Sending..."}</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-3.5 h-3.5" />
                            <span>{t("support.send") || "Submit Ticket"}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-sol-midnight/10 bg-white py-8 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-sol-midnight/60">
          <div className="flex items-center gap-4">
            <span className="font-medium text-sol-midnight">Solunary Platform</span>
            <span>·</span>
            <span>© {new Date().getFullYear()} Solunary. {t("footer.rights") || "All rights reserved."}</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="hover:text-sol-gold transition">
              {t("footer.terms") || "Terms and Conditions"}
            </Link>
            <Link to="/legal/privacy" className="hover:text-sol-gold transition">
              {t("footer.privacy") || "Privacy Policy"}
            </Link>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sol-gold hover:underline font-medium">
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
